/**
 * Product Form Service - Story 6.3: Create Add Product Form
 * API client for product creation and image upload endpoints
 * Service: Commerce Service (port 8083)
 */

import { microservicesClient } from '@/services/microservices/index';
import {
  CreateProductRequest,
  CreateProductResponse,
  UploadImageResponse,
} from '../types/productForm.types';
import { createProductRequestSchema } from '../schemas/productForm.schema';

// ============================================================================
// Constants
// ============================================================================

const API_BASE = '/api/v1/marketplace/products';

// ============================================================================
// Custom Error Class
// ============================================================================

/**
 * Custom error class for product form API errors
 * Extends Error to include HTTP status code and field-level validation errors
 */
export class ProductFormApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly fieldErrors?: Record<string, string>,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = 'ProductFormApiError';
    // Maintain proper stack trace for where error was thrown (only works in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ProductFormApiError);
    }
  }
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new product
 * POST /api/v1/marketplace/products
 *
 * @param request - Product creation request data
 * @returns Promise resolving to created product response
 * @throws ProductFormApiError on validation or API failure
 *
 * @example
 * const product = await createProduct({
 *   name: 'Biogas Fertilizer',
 *   category: 'GAUSHALA_PRODUCTS',
 *   description: 'High-quality organic fertilizer...',
 *   price: 500,
 *   unit: 'kg',
 *   quantity: 100,
 *   state: 'Maharashtra',
 *   district: 'Pune',
 *   pincode: '411001'
 * });
 */
export async function createProduct(
  request: CreateProductRequest,
  images: File[] = []
): Promise<CreateProductResponse> {
  try {
    console.log('[ProductFormService] Creating product:', {
      name: request.name,
      category: request.category,
      imagesCount: images.length,
    });

    // Validate request using Zod schema
    const validationResult = await createProductRequestSchema.safeParseAsync(request);

    if (!validationResult.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of validationResult.error.issues) {
        const path = issue.path.join('.');
        if (!fieldErrors[path]) {
          fieldErrors[path] = issue.message;
        }
      }
      throw new ProductFormApiError(
        'Validation failed',
        400,
        fieldErrors,
        'VALIDATION_ERROR'
      );
    }

    const validatedRequest = validationResult.data;

    // Map frontend field names to backend field names
    const backendRequest = {
      productName: validatedRequest.name,
      category: validatedRequest.category,
      description: validatedRequest.description,
      price: validatedRequest.price,
      unit: validatedRequest.unit,
      quantityAvailable: validatedRequest.quantity,
      minimumOrderQuantity: validatedRequest.minimumOrderQuantity,
    };

    console.log('[ProductFormService] Creating multipart request:', {
      productDTO: backendRequest,
      logoCount: images.length,
    });

    // Create FormData for multipart/form-data payload
    const formData = new FormData();

    // Add productDTO as JSON string (required by backend controller)
    formData.append('productDTO', JSON.stringify(backendRequest));

    // Add logo files (optional, backend parameter name is 'logo')
    if (images && images.length > 0) {
      images.forEach((file) => {
        formData.append('logo', file);
      });
    }

    console.log('[ProductFormService] Multipart payload ready with', images.length, 'images');

    // Call API endpoint with multipart/form-data
    const response = await microservicesClient.callService(
      'commerce-service',
      API_BASE,
      {
        method: 'POST',
        // DO NOT set Content-Type header - browser will set it with boundary for multipart
        headers: {
          // Remove 'Content-Type': 'application/json'
        },
        body: formData,
      }
    );

    // Handle error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[ProductFormService] API error creating product:', {
        status: response.status,
        error: errorData,
      });

      // Handle specific error codes
      if (response.status === 401) {
        throw new ProductFormApiError(
          'Unauthorized - Please login again',
          401,
          undefined,
          'UNAUTHORIZED'
        );
      }
      if (response.status === 403) {
        throw new ProductFormApiError(
          'Forbidden - No permission to create products',
          403,
          undefined,
          'FORBIDDEN'
        );
      }
      if (response.status === 400) {
        throw new ProductFormApiError(
          errorData.message || 'Invalid product data',
          400,
          errorData.fieldErrors,
          'BAD_REQUEST'
        );
      }

      throw new ProductFormApiError(
        errorData.message || 'Failed to create product',
        response.status,
        errorData.fieldErrors,
        'CREATE_ERROR'
      );
    }

    const data = await response.json();

    console.log('[ProductFormService] Raw backend response:', data);

    // Backend returns ProductResponse directly (not wrapped)
    // Map backend field names to frontend field names
    const mappedResponse: CreateProductResponse = {
      id: String(data.id || data.externalId),
      name: data.productName,
      category: data.category,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };

    console.log('[ProductFormService] Successfully created product:', {
      id: mappedResponse.id,
      name: mappedResponse.name,
    });

    return mappedResponse;
  } catch (error) {
    console.error('[ProductFormService] Error creating product:', error);

    // Re-throw ProductFormApiError as-is
    if (error instanceof ProductFormApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      throw new ProductFormApiError(
        error.message || 'Network error while creating product',
        500,
        undefined,
        'NETWORK_ERROR'
      );
    }

    throw new ProductFormApiError(
      'Unknown error creating product',
      500,
      undefined,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Upload images for a product
 * POST /api/v1/marketplace/products/{id}/images
 *
 * @param productId - ID of the product to upload images for
 * @param images - Array of File objects to upload (1-5 images)
 * @returns Promise resolving to array of uploaded image responses
 * @throws ProductFormApiError on validation or API failure
 *
 * @example
 * const uploadedImages = await uploadProductImages('product-123', [file1, file2]);
 */
export async function uploadProductImages(
  productId: string,
  images: File[]
): Promise<UploadImageResponse[]> {
  try {
    console.log('[ProductFormService] Uploading images for product:', {
      productId,
      imageCount: images.length,
    });

    // Validate product ID (convert to string if needed)
    const productIdStr = String(productId);
    if (!productIdStr || productIdStr.trim() === '') {
      throw new ProductFormApiError(
        'Product ID is required',
        400,
        { productId: 'Product ID is required' },
        'VALIDATION_ERROR'
      );
    }

    // Validate images array
    if (!Array.isArray(images) || images.length === 0) {
      throw new ProductFormApiError(
        'At least one image is required',
        400,
        { images: 'At least one image is required' },
        'VALIDATION_ERROR'
      );
    }

    if (images.length > 5) {
      throw new ProductFormApiError(
        'Maximum 5 images allowed',
        400,
        { images: 'Maximum 5 images allowed' },
        'VALIDATION_ERROR'
      );
    }

    // Validate individual images
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxFileSize = 10 * 1024 * 1024; // 10MB (matching backend)

    for (let i = 0; i < images.length; i++) {
      const file = images[i];
      if (!allowedTypes.includes(file.type)) {
        throw new ProductFormApiError(
          `Image ${i + 1}: Only JPEG, PNG, and WebP images are allowed`,
          400,
          { [`images.${i}`]: 'Invalid file type' },
          'VALIDATION_ERROR'
        );
      }
      if (file.size > maxFileSize) {
        throw new ProductFormApiError(
          `Image ${i + 1}: File size exceeds 10MB limit`,
          400,
          { [`images.${i}`]: 'File size too large' },
          'VALIDATION_ERROR'
        );
      }
    }

    // Create FormData with proper field names matching backend expectations
    const formData = new FormData();
    images.forEach((file) => {
      // Backend expects @RequestParam("images") MultipartFile[]
      formData.append('images', file);
    });

    console.log('[ProductFormService] Sending multipart request with', images.length, 'images');

    // Call API endpoint
    // Note: Don't set Content-Type header - browser will set it with boundary for multipart/form-data
    const response = await microservicesClient.callService(
      'commerce-service',
      `${API_BASE}/${productIdStr}/images`,
      {
        method: 'POST',
        body: formData,
        // Explicitly override to not set Content-Type (let browser handle it)
        headers: {},
      }
    );

    console.log('[ProductFormService] Image upload response status:', response.status);

    // Handle error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[ProductFormService] API error uploading images:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });

      if (response.status === 401) {
        throw new ProductFormApiError(
          'Unauthorized - Please login again',
          401,
          undefined,
          'UNAUTHORIZED'
        );
      }
      if (response.status === 403) {
        throw new ProductFormApiError(
          'Forbidden - No permission to upload images',
          403,
          undefined,
          'FORBIDDEN'
        );
      }
      if (response.status === 404) {
        throw new ProductFormApiError(
          'Product not found',
          404,
          undefined,
          'PRODUCT_NOT_FOUND'
        );
      }
      if (response.status === 413) {
        throw new ProductFormApiError(
          'File size too large',
          413,
          undefined,
          'PAYLOAD_TOO_LARGE'
        );
      }

      throw new ProductFormApiError(
        errorData.message || 'Failed to upload images',
        response.status,
        undefined,
        'UPLOAD_ERROR'
      );
    }

    const data = await response.json();

    console.log('[ProductFormService] Raw image upload response:', data);
    console.log('[ProductFormService] Response type:', typeof data, 'Is array:', Array.isArray(data));

    // Handle multiple response formats
    let imageUrls: string[] = [];

    // Format 1: { imageUrls: ["url1", "url2", ...] }
    if (data?.imageUrls && Array.isArray(data.imageUrls)) {
      imageUrls = data.imageUrls;
      console.log('[ProductFormService] Using imageUrls format');
    }
    // Format 2: Direct array: ["url1", "url2", ...]
    else if (Array.isArray(data)) {
      imageUrls = data;
      console.log('[ProductFormService] Using direct array format');
    }
    // Format 3: { data: { imageUrls: [...] } }
    else if (data?.data?.imageUrls && Array.isArray(data.data.imageUrls)) {
      imageUrls = data.data.imageUrls;
      console.log('[ProductFormService] Using nested data format');
    }
    // Format 4: Array of objects with url property
    else if (Array.isArray(data) && data[0]?.url) {
      imageUrls = data.map(item => item.url);
      console.log('[ProductFormService] Using array of objects format');
    }
    else if (data?.data && Array.isArray(data.data) && data.data[0]?.url) {
      imageUrls = data.data.map(item => item.url);
      console.log('[ProductFormService] Using nested data array of objects format');
    }

    if (!imageUrls || imageUrls.length === 0) {
      console.error('[ProductFormService] Invalid response structure - could not extract imageUrls:', data);
      throw new ProductFormApiError(
        'Invalid response structure from server - no image URLs found',
        500,
        undefined,
        'INVALID_RESPONSE'
      );
    }

    console.log('[ProductFormService] Successfully uploaded images:', {
      count: imageUrls.length,
      urls: imageUrls,
    });

    // Transform the response to match UploadImageResponse[] format
    const uploadResponses: UploadImageResponse[] = imageUrls.map((url: string, index: number) => ({
      imageId: `uploaded-${Date.now()}-${index}`,
      url: url,
      uploadedAt: new Date().toISOString(),
    }));

    console.log('[ProductFormService] Transformed upload responses:', uploadResponses);

    return uploadResponses;
  } catch (error) {
    console.error('[ProductFormService] Error uploading images:', error);

    // Re-throw ProductFormApiError as-is
    if (error instanceof ProductFormApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      throw new ProductFormApiError(
        error.message || 'Network error while uploading images',
        500,
        undefined,
        'NETWORK_ERROR'
      );
    }

    throw new ProductFormApiError(
      'Unknown error uploading images',
      500,
      undefined,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Update an existing product
 * PUT /api/v1/marketplace/products/{id}
 *
 * @param productId - ID of the product to update
 * @param request - Product update request data (partial updates supported)
 * @returns Promise resolving to updated product response
 * @throws ProductFormApiError on validation or API failure
 *
 * @example
 * const product = await updateProduct('product-123', {
 *   name: 'Updated Biogas Fertilizer',
 *   price: 550,
 *   quantity: 150
 * });
 */
export async function updateProduct(
  productId: string,
  request: Partial<CreateProductRequest>,
  existingImageUrls?: string[]
): Promise<CreateProductResponse> {
  try {
    console.log('[ProductFormService] Updating product:', {
      productId,
      fields: Object.keys(request),
    });

    // Validate product ID (convert to string if needed)
    const productIdStr = String(productId);
    if (!productIdStr || productIdStr.trim() === '') {
      throw new ProductFormApiError(
        'Product ID is required',
        400,
        { productId: 'Product ID is required' },
        'VALIDATION_ERROR'
      );
    }

    // Validate request using Zod schema (partial validation)
    // Only validate fields that are provided
    const partialSchema = createProductRequestSchema.partial();
    const validationResult = await partialSchema.safeParseAsync(request);

    if (!validationResult.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of validationResult.error.issues) {
        const path = issue.path.join('.');
        if (!fieldErrors[path]) {
          fieldErrors[path] = issue.message;
        }
      }
      throw new ProductFormApiError(
        'Validation failed',
        400,
        fieldErrors,
        'VALIDATION_ERROR'
      );
    }

    const validatedRequest = validationResult.data;

    // Map frontend field names to backend field names
    const backendRequest: Record<string, unknown> = {};
    if (validatedRequest.name !== undefined) backendRequest.productName = validatedRequest.name;
    if (validatedRequest.category !== undefined) backendRequest.category = validatedRequest.category;
    if (validatedRequest.description !== undefined) backendRequest.description = validatedRequest.description;
    if (validatedRequest.price !== undefined) backendRequest.price = validatedRequest.price;
    if (validatedRequest.unit !== undefined) backendRequest.unit = validatedRequest.unit;
    if (validatedRequest.quantity !== undefined) backendRequest.quantityAvailable = validatedRequest.quantity;
    if (validatedRequest.minimumOrderQuantity !== undefined) backendRequest.minimumOrderQuantity = validatedRequest.minimumOrderQuantity;

    // Include existing image URLs to keep (for edit mode image management)
    // This replaces the entire images list on the backend, removing deleted images
    if (existingImageUrls !== undefined) {
      backendRequest.images = existingImageUrls;
      console.log('[ProductFormService] Including existing images to keep:', existingImageUrls.length);
    }

    console.log('[ProductFormService] Mapped backend update request:', backendRequest);

    // Call API endpoint
    const response = await microservicesClient.callService(
      'commerce-service',
      `${API_BASE}/${productIdStr}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendRequest),
      }
    );

    // Handle error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[ProductFormService] API error updating product:', {
        status: response.status,
        error: errorData,
      });

      // Handle specific error codes
      if (response.status === 401) {
        throw new ProductFormApiError(
          'Unauthorized - Please login again',
          401,
          undefined,
          'UNAUTHORIZED'
        );
      }
      if (response.status === 403) {
        throw new ProductFormApiError(
          'Forbidden - No permission to update products',
          403,
          undefined,
          'FORBIDDEN'
        );
      }
      if (response.status === 404) {
        throw new ProductFormApiError(
          'Product not found',
          404,
          undefined,
          'PRODUCT_NOT_FOUND'
        );
      }
      if (response.status === 400) {
        throw new ProductFormApiError(
          errorData.message || 'Invalid product data',
          400,
          errorData.fieldErrors,
          'BAD_REQUEST'
        );
      }

      throw new ProductFormApiError(
        errorData.message || 'Failed to update product',
        response.status,
        errorData.fieldErrors,
        'UPDATE_ERROR'
      );
    }

    const data = await response.json();

    console.log('[ProductFormService] Raw backend update response:', data);

    // Backend returns ProductResponse directly (not wrapped)
    // Map backend field names to frontend field names
    const mappedResponse: CreateProductResponse = {
      id: String(data.id || data.externalId),
      name: data.productName,
      category: data.category,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };

    console.log('[ProductFormService] Successfully updated product:', {
      id: mappedResponse.id,
      name: mappedResponse.name,
    });

    return mappedResponse;
  } catch (error) {
    console.error('[ProductFormService] Error updating product:', error);

    // Re-throw ProductFormApiError as-is
    if (error instanceof ProductFormApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      throw new ProductFormApiError(
        error.message || 'Network error while updating product',
        500,
        undefined,
        'NETWORK_ERROR'
      );
    }

    throw new ProductFormApiError(
      'Unknown error updating product',
      500,
      undefined,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Create product with images in a combined operation
 * Handles partial success (product created but images failed)
 *
 * @param request - Product creation request data
 * @param images - Array of File objects to upload
 * @returns Promise resolving to product, uploaded images, and optional partial error
 * @throws ProductFormApiError if product creation fails
 *
 * @example
 * const { product, images, partialError } = await createProductWithImages(
 *   productData,
 *   imageFiles
 * );
 * if (partialError) {
 *   console.warn('Product created but images failed:', partialError.message);
 * }
 */
export async function createProductWithImages(
  request: CreateProductRequest,
  images: File[]
): Promise<{
  product: CreateProductResponse;
  images: UploadImageResponse[];
  partialError?: ProductFormApiError;
}> {
  let product: CreateProductResponse | null = null;

  try {
    // Step 1: Create the product with images in a single multipart request
    console.log('[ProductFormService] Starting product creation with images...');
    console.log('[ProductFormService] Images to upload:', images.length);

    // Pass images to createProduct - backend handles both creation and image storage in one request
    product = await createProduct(request, images);

    console.log('[ProductFormService] Product created with images successfully');

    // Return successful response (images are already saved in the same request)
    return {
      product,
      images: [], // Images are handled in the same request, no separate upload needed
    };
  } catch (error) {
    // If product creation fails, throw the error
    console.error('[ProductFormService] Product creation failed:', error);
    throw error;
  }
}

/**
 * Update product with images in a two-step operation (based on cattle management pattern)
 * Step 1: Update product metadata (JSON body)
 * Step 2: Upload images separately (multipart)
 *
 * Handles partial success (product updated but images failed)
 *
 * @param productId - ID of the product to update
 * @param request - Product update request data
 * @param images - Array of new File objects to upload (only actual files, not existing image IDs)
 * @returns Promise resolving to product, uploaded images, and optional partial error
 * @throws ProductFormApiError if product update fails
 *
 * @example
 * const { product, images, partialError } = await updateProductWithImages(
 *   'product-123',
 *   productData,
 *   newImageFiles
 * );
 * if (partialError) {
 *   console.warn('Product updated but images failed:', partialError.message);
 * }
 */
export async function updateProductWithImages(
  productId: string,
  request: Partial<CreateProductRequest>,
  images: File[],
  existingImageUrls?: string[]
): Promise<{
  product: CreateProductResponse;
  images: UploadImageResponse[];
  partialError?: ProductFormApiError;
}> {
  let product: CreateProductResponse | null = null;

  try {
    // Step 1: Update the product with JSON body (metadata + existing image URLs to keep)
    // The backend replaces the entire images list, removing any images the user deleted in the UI
    console.log('[ProductFormService] Step 1: Updating product metadata + image list...', {
      productId,
      hasNewImages: images.length > 0,
      existingImagesToKeep: existingImageUrls?.length ?? 'not provided',
      fields: Object.keys(request),
    });
    product = await updateProduct(productId, request, existingImageUrls);
    console.log('[ProductFormService] Product metadata and image list updated successfully');
  } catch (error) {
    // If product update fails, throw the error
    console.error('[ProductFormService] Product metadata update failed:', error);
    throw error;
  }

  // Step 2: Upload new images separately (only if product update succeeded and images provided)
  if (images.length === 0) {
    console.log('[ProductFormService] No new images to upload');
    return {
      product,
      images: [],
    };
  }

  try {
    console.log('[ProductFormService] Step 2: Uploading new images...', {
      productId: product.id,
      imageCount: images.length,
    });
    const uploadedImages = await uploadProductImages(product.id, images);

    console.log('[ProductFormService] Product updated with images successfully');
    return {
      product,
      images: uploadedImages,
    };
  } catch (error) {
    // If image upload fails, return product with partial error
    // Product was updated successfully, but images failed
    console.warn(
      '[ProductFormService] Product updated but image upload failed:',
      error
    );

    const partialError =
      error instanceof ProductFormApiError
        ? error
        : new ProductFormApiError(
            'Failed to upload images. Product was updated, but images could not be uploaded. Please try uploading images again.',
            500,
            undefined,
            'IMAGE_UPLOAD_ERROR'
          );

    return {
      product,
      images: [],
      partialError,
    };
  }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if an error is a ProductFormApiError
 * @param error - Unknown error to check
 * @returns True if error is ProductFormApiError
 */
export function isProductFormApiError(error: unknown): error is ProductFormApiError {
  return error instanceof ProductFormApiError;
}

/**
 * Type guard to validate CreateProductResponse structure
 * @param data - Unknown data to validate
 * @returns True if data matches CreateProductResponse structure
 */
export function isCreateProductResponse(data: unknown): data is CreateProductResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    'id' in data &&
    typeof (data as CreateProductResponse).id === 'string' &&
    'name' in data &&
    typeof (data as CreateProductResponse).name === 'string' &&
    'category' in data &&
    'status' in data &&
    typeof (data as CreateProductResponse).status === 'string'
  );
}

/**
 * Type guard to validate UploadImageResponse structure
 * @param data - Unknown data to validate
 * @returns True if data matches UploadImageResponse structure
 */
export function isUploadImageResponse(data: unknown): data is UploadImageResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    'imageId' in data &&
    typeof (data as UploadImageResponse).imageId === 'string' &&
    'url' in data &&
    typeof (data as UploadImageResponse).url === 'string' &&
    'uploadedAt' in data &&
    typeof (data as UploadImageResponse).uploadedAt === 'string'
  );
}
